<template>
  <div class="resourceFormPage divContainer aiRoleReviewInfoPage">
    <div v-if="loading" v-loading="loading" class="fullContainer" />

    <div v-else class="resourceFormPage__layout">
      <div class="resourceFormPage__headerDock">
        <header class="resourceFormPage__actionBar">
          <div class="resourceFormPage__lead">
            <button type="button" class="resourceFormPage__backLink" @click="goBack">
              <i class="el-icon-arrow-left" aria-hidden="true" />
              返回列表
            </button>
            <h1 class="resourceFormPage__pageTitle">AI 角色审核</h1>
            <span
              v-if="info.reviewStatus != null"
              class="statusPill"
              :class="'statusPill--' + statusPillVariant(info.reviewStatus)"
            >
              <span class="statusPill__dot" aria-hidden="true" />
              {{ statusLabel(info.reviewStatus) }}
            </span>
          </div>
        </header>
      </div>

      <main class="resourceFormPage__main main">
        <section class="auditDetailSection">
          <div class="auditDetailSection__head">
            <h2 class="auditDetailSection__title">基本信息</h2>
          </div>

          <div class="auditDetailBasic">
            <div class="auditDetailBasic__aside">
              <div class="auditDetailBasic__preview">
                <img
                  v-if="info.thumbUrl"
                  :src="info.thumbUrl"
                  alt="角色头像"
                  class="auditDetailThumb auditDetailThumb--hero"
                />
                <div v-else class="auditDetailThumb auditDetailThumb--empty">
                  <i class="el-icon-user" aria-hidden="true" />
                </div>
              </div>
              <h3 class="auditDetailBasic__name">{{ info.name || '—' }}</h3>
              <span
                class="auditDetailBasic__typeTag"
                :class="roleTypeTagClass"
              >
                {{ roleTypeLabel }}
              </span>
            </div>

            <div class="auditDetailBasic__main">
              <div class="auditDetailMetaGrid">
                <div class="auditDetailMetaGrid__cell">
                  <div class="auditDetailMetaGrid__label">用户 id</div>
                  <div class="auditDetailMetaGrid__value">{{ info.uid || '—（官方 PGC）' }}</div>
                </div>
                <div class="auditDetailMetaGrid__cell">
                  <div class="auditDetailMetaGrid__label">创建时间</div>
                  <div class="auditDetailMetaGrid__value">{{ info.createTime || '—' }}</div>
                </div>
                <div class="auditDetailMetaGrid__cell">
                  <div class="auditDetailMetaGrid__label">可见性</div>
                  <div class="auditDetailMetaGrid__value">{{ visibilityLabel }}</div>
                </div>
                <div class="auditDetailMetaGrid__cell">
                  <div class="auditDetailMetaGrid__label">角色 Key</div>
                  <div class="auditDetailMetaGrid__value auditDetailMetaGrid__value--mono">
                    {{ info.key || '—' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="auditPromptSection">
            <div class="auditPromptSection__head">
              <h3 class="auditPromptSection__title">角色 Prompt 对照</h3>
              <p class="auditPromptSection__hint">
                「原文」是用户/App 提交的原始描述（机审读这份）；「中文译文」是机审通过后后台自动翻译的结果，供运营阅读，不一定与原文逐字对应。
              </p>
            </div>
            <div class="auditPromptCompare">
              <div class="auditPromptPanel">
                <div class="auditPromptPanel__label">Prompt（原文）</div>
                <div class="auditDetailTextBlock auditDetailTextBlock--prompt">
                  {{ originalPrompt || '—' }}
                </div>
              </div>
              <div class="auditPromptPanel">
                <div class="auditPromptPanel__label">Prompt（中文译文）</div>
                <div class="auditDetailTextBlock auditDetailTextBlock--prompt">
                  {{ translatedPrompt || '—（机审未完成或翻译失败时为空）' }}
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="isSelfApprovalBlocked"
            class="auditDetailNotice auditDetailNotice--warning"
            role="status"
          >
            本条记录的提交人与当前登录账号相同。按后端规则，<strong>你不能对自己提交的修改点「通过」</strong>，需换另一位运营账号审核；「驳回」不受此限制。
          </div>
        </section>

        <section v-if="historyList.length" class="auditDetailSection">
          <div class="auditDetailSection__head">
            <h2 class="auditDetailSection__title">历史记录</h2>
          </div>
          <div class="auditHistoryList">
            <article
              v-for="(item, index) in historyList"
              :key="index"
              class="auditHistoryItem"
            >
              <div>
                <div class="auditHistoryItem__label">审核人</div>
                <div class="auditHistoryItem__value">{{ item.reviewerName || '—' }}</div>
              </div>
              <div>
                <div class="auditHistoryItem__label">审核意见</div>
                <div class="auditHistoryItem__value">{{ item.comment || '—' }}</div>
              </div>
              <div>
                <div class="auditHistoryItem__label">状态</div>
                <span
                  class="statusPill"
                  :class="'statusPill--' + statusPillVariant(item.reviewStatus)"
                >
                  <span class="statusPill__dot" aria-hidden="true" />
                  {{ statusLabel(item.reviewStatus) }}
                </span>
              </div>
              <div>
                <div class="auditHistoryItem__label">审核时间</div>
                <div class="auditHistoryItem__value">{{ item.createTime || '—' }}</div>
              </div>
            </article>
          </div>
        </section>
      </main>

      <footer class="resourceFormPage__footer">
        <el-button
          class="actionButton actionButtonGhost"
          size="small"
          @click="rejectPrompt"
        >
          驳回
        </el-button>
        <el-button
          class="actionButton actionButtonQuery"
          type="primary"
          size="small"
          :disabled="isSelfApprovalBlocked"
          @click="resolvePrompt"
        >
          通过
        </el-button>
      </footer>
    </div>

    <el-dialog
      title="驳回原因"
      :visible.sync="rejectDialogVisible"
      width="420px"
      custom-class="auditRejectDialog"
      :before-close="closeRejectDialog"
    >
      <p class="auditRejectDialog__hint">请填写驳回原因，便于创作者后续修改。</p>
      <el-input
        v-model="rejectComment"
        size="small"
        type="textarea"
        :rows="3"
        placeholder="请输入驳回原因"
      />
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="closeRejectDialog">取消</el-button>
        <el-button size="small" type="primary" @click="confirmReject">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getAiRoleReviewById, updateAiRoleReviewStatus } from '../../../api/aiRoleReview'
import {
  roleReviewStatusLabel,
  roleReviewStatusPillVariant,
} from '../aiAuditStatus'

export default {
  name: 'AiRoleReviewInfo',
  data() {
    return {
      info: {},
      loading: false,
      rejectComment: '',
      rejectDialogVisible: false,
    }
  },
  computed: {
    historyList() {
      return Array.isArray(this.info.reviewHistoryList) ? this.info.reviewHistoryList : []
    },
    originalPrompt() {
      const jsonData = this.info.jsonData
      if (!jsonData) return ''
      return jsonData.prompt || ''
    },
    translatedPrompt() {
      return this.info.desc || ''
    },
    roleTypeLabel() {
      return this.info.uid ? 'UGC（用户创建）' : 'PGC（官方运营）'
    },
    roleTypeTagClass() {
      return this.info.uid ? 'auditDetailBasic__typeTag--ugc' : 'auditDetailBasic__typeTag--pgc'
    },
    visibilityLabel() {
      const visibility = this.info.visibility
      if (!visibility) return '—'
      if (visibility === 'Public') return '公开'
      if (visibility === 'Private') return '私有'
      return visibility
    },
    currentLoginUid() {
      try {
        const raw = localStorage.getItem('user_info')
        if (!raw) return ''
        const parsed = JSON.parse(raw)
        return (parsed.userInfo && parsed.userInfo.uid) || parsed.uid || ''
      } catch (e) {
        return ''
      }
    },
    isSelfApprovalBlocked() {
      const submitUser = this.info.submitUser
      if (!submitUser || !this.currentLoginUid) return false
      return String(submitUser) === String(this.currentLoginUid)
    },
  },
  created() {
    this.loadDetail()
  },
  methods: {
    statusLabel: roleReviewStatusLabel,
    statusPillVariant: roleReviewStatusPillVariant,
    goBack() {
      this.$router.push('/tools/aiRoleReview/list')
    },
    loadDetail() {
      this.loading = true
      getAiRoleReviewById({ id: this.$route.params.id })
        .then((res) => {
          if (res.success) {
            this.info = res.data || {}
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    changeStatus(status) {
      updateAiRoleReviewStatus({
        id: this.$route.params.id,
        status,
        comment: this.rejectComment,
      })
        .then((res) => {
          if (res.success) {
            this.$message.success('状态更新成功')
            this.$router.push('/tools/aiRoleReview/list')
          }
        })
        .catch((err) => {
          this.$message.error(String(err))
        })
        .finally(() => {
          this.closeRejectDialog()
        })
    },
    closeRejectDialog() {
      this.rejectComment = ''
      this.rejectDialogVisible = false
    },
    rejectPrompt() {
      this.rejectDialogVisible = true
    },
    confirmReject() {
      if (!String(this.rejectComment || '').trim()) {
        this.$message.warning('请填写驳回原因')
        return
      }
      this.changeStatus(2)
    },
    resolvePrompt() {
      if (this.isSelfApprovalBlocked) {
        this.$message.warning('修改和审核人不能是同一个人，请换账号审核')
        return
      }
      this.rejectComment = '审核通过'
      this.changeStatus(1)
    },
  },
}
</script>
